import React, { useState, useEffect, useRef } from 'react';
import { Drawer } from '@mui/material';
import { useSwipeable } from 'react-swipeable';
import { useShoppingList } from '../../context/ShoppingListContext';
import ShoppingList from './ShoppingList';
import InquiryForm from '../../forms/InquiryForm';

type Props = {
  isShoppingListOpen: boolean;
  setIsShoppingListOpen: (isOpen: boolean) => void;
};

function ShoppingPanel({ isShoppingListOpen, setIsShoppingListOpen }: Props) {
  const { shoppingList, removeProductFromShoppingList, setProductQuantity } =
    useShoppingList();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [swipePosition, setSwipePosition] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  // Użycie useRef zamiast useState dla elementu DOM
  const drawerPaperRef = useRef<HTMLElement | null>(null);

  // Nasłuchiwanie zmiany rozmiaru okna
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Inicjalne sprawdzenie
    checkIfMobile();

    // Nasłuchiwanie zmian rozmiaru okna
    window.addEventListener('resize', checkIfMobile);

    // Czyszczenie listenera przy odmontowaniu komponentu
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Reset swipe position when drawer opens
  useEffect(() => {
    if (isShoppingListOpen) {
      setSwipePosition(0);
      setIsClosing(false);

      // Reset transformacji przy otwarciu panelu
      if (drawerPaperRef.current) {
        drawerPaperRef.current.style.transform = '';
        drawerPaperRef.current.style.transition = '';
      }
    }
  }, [isShoppingListOpen]);

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const swipeThreshold = 100; // Próg przesunięcia w pikselach do zamknięcia

  // Obsługa swipe
  const handlers = useSwipeable({
    onSwiping: (event) => {
      if (!isMobile || event.dir !== 'Down') return;

      // Ustawienie pozycji przeciągnięcia (tylko w dół)
      const newPosition = Math.max(0, event.deltaY);
      setSwipePosition(newPosition);

      // Zastosuj transformację do całego panelu
      if (drawerPaperRef.current) {
        drawerPaperRef.current.style.transform = `translateY(${newPosition}px)`;
        drawerPaperRef.current.style.transition = 'none'; // Wyłącz transition podczas przeciągania
      }
    },
    onSwipedDown: (event) => {
      if (!isMobile) return;

      if (event.deltaY > swipeThreshold) {
        // Jeśli przesunięcie przekroczyło próg, zamknij panel
        setIsClosing(true);

        // Płynna animacja zamykania
        if (drawerPaperRef.current) {
          drawerPaperRef.current.style.transition = 'transform 0.3s ease-out';
          drawerPaperRef.current.style.transform = 'translateY(100%)';
        }

        // Zamknij panel po zakończeniu animacji
        setTimeout(() => {
          setIsShoppingListOpen(false);
        }, 300);
      } else {
        // Jeśli nie przekroczono progu, wróć do pozycji początkowej
        if (drawerPaperRef.current) {
          drawerPaperRef.current.style.transition = 'transform 0.3s ease-out';
          drawerPaperRef.current.style.transform = 'translateY(0)';
        }
        setSwipePosition(0);
      }
    },
    onSwiped: () => {
      // Reset transitions when touch ends
      if (
        drawerPaperRef.current &&
        swipePosition < swipeThreshold &&
        !isClosing
      ) {
        drawerPaperRef.current.style.transition = 'transform 0.3s ease-out';
        drawerPaperRef.current.style.transform = 'translateY(0)';
        setTimeout(() => {
          if (drawerPaperRef.current) {
            drawerPaperRef.current.style.transition = '';
          }
          setSwipePosition(0);
        }, 300);
      }
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: true,
    delta: 10,
  });

  return (
    <>
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={isShoppingListOpen}
        onClose={() => setIsShoppingListOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '450px',
            maxWidth: '100%',
            height: isMobile ? '80%' : '100%',
            maxHeight: isMobile
              ? 'calc(80vh - env(safe-area-inset-bottom))'
              : '100vh',
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 16px)' : 0,
            borderTopLeftRadius: isMobile ? '16px' : 0,
            borderTopRightRadius: isMobile ? '16px' : 0,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
            overflowY: 'hidden',
            touchAction: isMobile ? 'pan-x' : 'auto', // Blokuje tylko vertical swipe
            willChange: 'transform',
          },
          ref: (el: any) => {
            drawerPaperRef.current = el;
            if (isMobile && el) {
              handlers.ref(el);
            }
          },
        }}
        transitionDuration={{
          enter: 350,
          exit: 300,
        }}
        SlideProps={{
          direction: isMobile ? 'up' : 'left',
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <div className="h-full">
          {/* Uchwyt do przeciągania na urządzeniach mobilnych */}
          {isMobile && (
            <div className="flex justify-center py-2 cursor-grab active:cursor-grabbing touch-pan-y">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
          )}

          <ShoppingList
            shoppingList={shoppingList}
            removeProductFromShoppingList={removeProductFromShoppingList}
            setProductQuantity={setProductQuantity}
            setIsShoppingListOpen={setIsShoppingListOpen}
            handleDialogOpen={handleDialogOpen}
          />
        </div>
      </Drawer>

      <InquiryForm
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        products={shoppingList.map((product) => ({
          productId: product.id,
          quantity: product.quantity,
        }))}
        singleProduct={false}
      />
    </>
  );
}

export default ShoppingPanel;
